import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "@/lib/api";
import { useScope } from "@/state/scope";

import {
  disconnectOpenBudget,
  getAccounts,
  getCashflowMonthly,
  getMe,
  getNetWorthHistory,
  getOpenBudgetStatus,
  getOverview,
  getRecurring,
  getRunway,
  getSpendingByCategory,
  getSyncStatus,
  getTransactions,
  logout,
  patchAccountScope,
  requestCode,
  triggerSync,
  verifyCode,
} from "./endpoints";
import { isDataQuery, qk } from "./keys";
import type { AccountScope, AccountsResponse, BackfillState, SyncResponse, SyncRun, TransactionFilters } from "./types";

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: getMe, retry: false, staleTime: Infinity, gcTime: Infinity });
}

export function useOverview(month: string) {
  const { scope } = useScope();
  return useQuery({
    queryKey: qk.overview(scope, month),
    queryFn: () => getOverview(scope, month),
    placeholderData: keepPreviousData,
  });
}

export function useAccounts() {
  return useQuery({ queryKey: qk.accounts(), queryFn: getAccounts });
}

export function useUpdateAccountScope() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: AccountScope }) => patchAccountScope(id, scope),
    onMutate: async ({ id, scope }) => {
      await queryClient.cancelQueries({ queryKey: qk.accounts() });
      const previous = queryClient.getQueryData<AccountsResponse>(qk.accounts());
      if (previous) {
        queryClient.setQueryData<AccountsResponse>(qk.accounts(), {
          ...previous,
          accounts: previous.accounts.map((a) => (a.id === id ? { ...a, scope, scopeOverridden: true } : a)),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(qk.accounts(), context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ predicate: isDataQuery }),
  });
}

export function useTransactions(filters: TransactionFilters, options: { enabled?: boolean } = {}) {
  const { scope } = useScope();
  const key = { ...filters, scope };
  return useQuery({
    queryKey: qk.transactions(key),
    queryFn: () => getTransactions(key),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useSpendingByCategory(from: string, to: string) {
  const { scope } = useScope();
  return useQuery({
    queryKey: qk.spending(scope, from, to),
    queryFn: () => getSpendingByCategory(scope, from, to),
    placeholderData: keepPreviousData,
  });
}

export function useCashflowMonthly(months: number) {
  const { scope } = useScope();
  return useQuery({
    queryKey: qk.cashflow(scope, months),
    queryFn: () => getCashflowMonthly(scope, months),
    placeholderData: keepPreviousData,
  });
}

export function useNetWorthHistory(days: number) {
  const { scope } = useScope();
  return useQuery({
    queryKey: qk.networth(scope, days),
    queryFn: () => getNetWorthHistory(scope, days),
    placeholderData: keepPreviousData,
  });
}

export function useRecurring() {
  const { scope } = useScope();
  return useQuery({ queryKey: qk.recurring(scope), queryFn: () => getRecurring(scope) });
}

export function useRunway(span: number) {
  const { scope } = useScope();
  return useQuery({ queryKey: qk.runway(scope, span), queryFn: () => getRunway(scope, span), placeholderData: keepPreviousData });
}

export function useOpenBudgetStatus() {
  return useQuery({ queryKey: qk.obStatus, queryFn: getOpenBudgetStatus, staleTime: 30_000 });
}

export function useSyncStatus() {
  return useQuery({
    queryKey: qk.syncStatus,
    queryFn: getSyncStatus,
    refetchInterval: (query) => (query.state.data?.running ? 2000 : false),
  });
}

export type SyncProgress = { runs: number; lastRun: SyncRun; backfill: BackfillState };

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs a sync and, while the initial backfill is not finished, keeps calling the
 * endpoint (each call processes a bounded number of pages) so the whole history
 * lands without a single long request.
 */
export function useSync() {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<SyncProgress | null>(null);
  const mutation = useMutation({
    mutationFn: async (input?: { mode?: "incremental" | "backfill" | "full" }): Promise<SyncResponse> => {
      let runs = 0;
      let waits = 0;
      let last: SyncResponse;
      for (;;) {
        try {
          last = await triggerSync(runs === 0 && input?.mode ? { mode: input.mode } : {});
        } catch (error) {
          if (error instanceof ApiError && error.code === "sync_running" && waits++ < 30) {
            await wait(2000);
            continue;
          }
          throw error;
        }
        runs += 1;
        setProgress({ runs, lastRun: last.run, backfill: last.backfill });
        void queryClient.invalidateQueries({ queryKey: qk.syncStatus });
        if (last.run.status !== "ok" || last.backfill.done || runs >= 60) break;
      }
      return last;
    },
    onSettled: () => queryClient.invalidateQueries({ predicate: isDataQuery }),
  });
  return { ...mutation, progress };
}

export function useRequestCode() {
  return useMutation({ mutationFn: (email: string) => requestCode(email), retry: 0 });
}

export function useVerifyCode() {
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) => verifyCode(email, code),
    retry: 0,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    retry: 0,
    onSettled: () => queryClient.clear(),
  });
}

export function useDisconnectOpenBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectOpenBudget,
    onSettled: () => queryClient.invalidateQueries({ predicate: isDataQuery }),
  });
}
